/* eslint-disable import/no-unresolved */
import _ from 'lodash';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/virtual';
import { DemoConsumer } from 'contexts/HeroDemoProvider';
import { motion, useAnimationControls } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Image from 'components/Image';
import makeClasses from 'hooks/useClasses';
import PropTypes from 'prop-types';
import React, { useRef, useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import styles from 'styles/jss/components/landingHeroStyle';
import TitleTyper from 'components/Home/HeroDemo/TitleTyper';
import Typography from '@mui/material/Typography';

const PHONE_SRC = '/img/marketing/landing/section_hero_demo/phone.svg';

const initVariants = (page) => ({
  screenFlash: {
    default: {
      opacity: 0,
      transition: { duration: 0 }
    },
    active: {
      opacity: [0, 0.9, 0.7, 0],
      transition: {
        type: 'tween',
        duration: 0.8,
        time: [0, 0.06, 0.1, 1],
        delay: 0.5
      }
    }
  },

  slider: {
    default: {
      clipPath: `polygon(
        0 0,
        100% 0,
        100% 100%,
        0% 100%
      )`,
      transition: { duration: 0 }
    },
    active: {
      clipPath: `polygon(
        0 100%,
        100% 100%,
        100% 100%,
        0% 100%
      )`,
      transition: {
        type: 'tween',
        ease: 'easeInOut',
        duration: 2
      }
    }
  },

  shadow: {
    default: {
      clipPath: `polygon(
        0 0,
        100% 0,
        100% 100%,
        0% 100%
      )`,
      transition: { duration: 0 }
    },
    active: {
      clipPath: `polygon(
        0 0,
        100% 0,
        100% 1%,
        0% 1%
      )`,
      transition: {
        type: 'tween',
        ease: 'backInOut',
        duration: 1
      }
    }
  },

  laser: {
    default: {
      top: -5,
      transition: { duration: 0 }
    },
    down: {
      top: '101%',
      transition: {
        type: 'tween',
        ease: 'easeInOut',
        duration: 2
      }
    },
    up: {
      top: -5,
      transition: {
        type: 'tween',
        ease: 'backInOut',
        duration: 1
      }
    }
  },

  cameraControls: {
    default: {
      opacity: 1,
      transition: { duration: 0 }
    },
    disappear: {
      opacity: 0,
      transition: {
        default: { duration: 0.5 }
      }
    }
  },

  typerBox: {
    default: {
      // backgroundColor: 'rgba(0, 0, 0, 0)',
      opacity: 0,
      scale: 0.5,
      transition: { duration: 0 }
    },
    active: {
      opacity: 1,
      scale: 1,
      // borderColor: 'rgba(0, 0, 0, 1)',
      transition: {
        default: { duration: 0.4 },
        scale: {
          type: 'spring',
          stiffness: 211,
          mass: 1,
          damping: 10
        }
      }
    }
  },

  screenTyper: {
    default: {
      '--x': -1,
      transition: { duration: 0 }
    },
    active: {
      '--x': page.title.length,
      transition: {
        default: {
          ease: 'backInOut',
          duration: page.title.length * 0.2
        }
      }
    }
  },

  grid: {
    default: {
      opacity: 1,
      transition: { duration: 0 }
    },
    active: {
      opacity: 0,
      transition: {
        ease: 'backInOut',
        duration: 1
      }
    }
  }
});

const Slide = ({
  page,
  isActive,
  flashButtonControls,
  pageIndex,
  bookLength,
  swiperRef
}) => {
  const slideRef = useRef(null);
  const classes = makeClasses(styles);
  const { bookRef, setCurrentSlide, pageControls } = DemoConsumer();
  const [variants, setVariants] = useState();
  const sliderControls = useAnimationControls();
  const shadowControls = useAnimationControls();
  const flashControls = useAnimationControls();
  const cameraControls = useAnimationControls();
  const typerControls = useAnimationControls();
  const gridControls = useAnimationControls();
  const pageTyperControls = useAnimationControls();
  const typerBoxControls = useAnimationControls();
  const laserControls = useAnimationControls();
  const currentIndex = _.get(swiperRef, 'current.swiper.realIndex', 0);
  const exitDelay = pageIndex % 2 === 0 ? 0 : 1000;

  useEffect(() => {
    setVariants(initVariants(page));
  }, []);

  useEffect(() => {
    const pageControlsLoaded = pageControls.length === bookLength;

    (async () => {
      if (pageControlsLoaded && isActive) {
        setTimeout(async () => {
          await setCurrentSlide({
            pageIndex,
            pageTyperControls
          });

          // Turn to page
          bookRef.current.pageFlip().flip(pageIndex);
          // Trigger the flash button
          flashButtonControls.start('active');
          // Start screen sparkles
          pageControls[pageIndex].sparkleControls.start('active');
          // Flash the screen
          await flashControls.start('active');

          // Swipe away the background
          laserControls.start('down');
          cameraControls.start('disappear');
          await sliderControls.start('active');

          // Swipe away the shadow
          laserControls.start('up');
          // Fade in page image
          pageControls[pageIndex].imageControls.start('active');
          // Turn off sparkle
          pageControls[pageIndex].sparkleControls.start('default');
          await shadowControls.start('active');

          // Fade in background color
          pageControls[pageIndex].backgroundControls.start('active');
          // Fade out grid
          await gridControls.start('active');

          // Type text
          typerBoxControls.start('active');
          pageControls[pageIndex].pageTyperControls.start('active');
          await typerControls.start('active');

          setTimeout(() => {
            // Advance slide
            if (swiperRef.current) {
              swiperRef.current.swiper.slideNext();
            }
          }, exitDelay);
        }, 0);
      }
    })();
  }, [isActive, currentIndex, pageControls]);

  /**
   * Reset the slide animations once it's NOT active
   */
  useEffect(() => {
    if (!isActive) {
      // Give it a buffer so all the animations aren't working at the
      // beginning of the slide transition
      setTimeout(() => {
        // Check to see if it's unmounted
        if (slideRef.current) {
          cameraControls.set('default');
          typerControls.set('default');
          typerBoxControls.start('default');
          sliderControls.set('default');
          gridControls.set('default');
          shadowControls.set('default');
        }
      }, 500);
    }
  }, [isActive]);

  if (!variants) return null;
  return (
    <Box
      ref={slideRef}
      className={classes.phoneSlide}
      style={{
        backgroundColor: page.bgColor
      }}
    >
      <Box
        className="imageArea"
      >
        <motion.div
          className="cameraFlash"
          animate={flashControls}
          variants={variants.screenFlash}
          initial="default"
        />
        <Box
          className="editScreen"
        >
          <motion.div
            className="gridBackground"
            animate={gridControls}
            variants={variants.grid}
            initial="default"
          />
          <motion.div
            className="ogBackground"
            animate={sliderControls}
            variants={variants.slider}
            initial="default"
          >
            <Image
              src={page.screenBackground}
              layout="fill"
              objectFit="cover"
            />
          </motion.div>
          <motion.div
            className="ogShadow"
            animate={shadowControls}
            variants={variants.shadow}
            initial="default"
          >
            {page.screenShadow && (
              <Image
                src={page.screenShadow}
                layout="fill"
                objectFit="contain"
              />
            )}
          </motion.div>
          <Image
            src={page.screenMasked}
            layout="fill"
            objectFit="contain"
          />
          <motion.div
            layout="position"
            className="laser"
            animate={laserControls}
            variants={variants.laser}
            initial="default"
          />
          <motion.div
            className="cameraControls"
            animate={cameraControls}
            variants={variants.cameraControls}
            initial="default"
          >
            <Avatar>
              1.0x
            </Avatar>
          </motion.div>
          <motion.div
            className={classes.typer}
            animate={typerBoxControls}
            variants={variants.typerBox}
            initial="default"
          >
            <Typography
              className="typerTitle"
              color="#333" // {page.textColor}
              sx={{
                minHeight: 40
              }}
            >
              {isActive && (
                <TitleTyper
                  controls={typerControls}
                  variants={variants.screenTyper}
                  initial="default"
                  text={page.title}
                />
              )}
            </Typography>
          </motion.div>
        </Box>
      </Box>
    </Box>
  );
};

Slide.propTypes = {
  page: PropTypes.shape().isRequired,
  isActive: PropTypes.bool.isRequired,
  flashButtonControls: PropTypes.shape().isRequired,
  bookLength: PropTypes.number.isRequired,
  pageIndex: PropTypes.number.isRequired,
  swiperRef: PropTypes.shape().isRequired
};

const cameraButtonVariants = {
  default: {
    borderWidth: 2,
    transition: { duration: 0 }
  },
  active: {
    borderWidth: [2, 4, 2],
    transition: {
      type: 'tween',
      ease: 'backInOut',
      duration: 0.8,
      time: [0, 0.06, 1],
      delay: 0.5
    }
  }
};

const HeroDemoPhone = React.memo(({ pages }) => {
  const classes = makeClasses(styles);
  const swiperRef = useRef(null);
  const flashButtonControls = useAnimationControls();

  return (
    <Box
      className={classes.phone}
      sx={{
        height: '100%',
        width: '100%'
      }}
    >
      <Image
        src={PHONE_SRC}
        alt="Scribble Artwork"
        layout="fill"
        objectFit="contain"
      />
      <Box className="phoneScreen">
        <Stack className="cameraMode">
          <Box className="topBar">
            <Image
              src="/img/marketing/landing/section_hero_demo/app_logo@2x.png"
              width={81}
              height={20}
              layout="fixed"
              priority
            />
          </Box>
          <Box className="cameraScreen">
            <Swiper
              loop
              ref={swiperRef}
              className="screenSwiper"
              modules={[]}
              spaceBetween={0}
              slidesPerView={1}
              allowTouchMove={false}
            >
              {pages.map((p, i) => (
                <SwiperSlide
                  virtualIndex={i}
                  className=""
                  key={p.image}
                >
                  {({ isActive }) => (
                    <Slide
                      key={p.image}
                      page={p}
                      pageIndex={i}
                      isActive={isActive}
                      flashButtonControls={flashButtonControls}
                      swiperRef={swiperRef}
                      bookLength={pages.length}
                    />
                  )}
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
          <Box className="bottomBar">
            <Box className="shutter">
              <motion.div
                layout
                className="inner"
                animate={flashButtonControls}
                variants={cameraButtonVariants}
                initial="default"
              />
            </Box>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
});

HeroDemoPhone.propTypes = {
  pages: PropTypes.arrayOf(PropTypes.shape()).isRequired
};

export default HeroDemoPhone;
